import axios from 'axios';
import { handleRequestError } from '../../requests';

// Pricing domain: stored logic definitions (conditional charges). Expressions are posted as JSON strings
// so the API cannot reinterpret values inside them; responses return them as objects.
const BASE = '/logic-definitions';

export interface PricingRequestError extends Error {
  status?: number;
  issues: { severity: string; path: string; code: string; message: string }[];
}

const toPricingError = (fallback: string) => (error: any): never => {
  const data = error?.response?.data;
  const message = (typeof data === 'string' && data) || data?.message || fallback;
  throw Object.assign(new Error(message), { status: error?.response?.status, issues: data?.issues ?? [] }) as PricingRequestError;
};

export const getLogicDefinitions = async (params: { purpose?: string; ownerType?: string; ownerId?: string; includeRetired?: boolean }) => {
  const response = await axios.get(BASE, { params }).catch(handleRequestError);
  return response.data;
};

export const getLogicDefinitionHistory = async (lineageId: string) => {
  const response = await axios.get(`${BASE}/lineages/${lineageId}`).catch(handleRequestError);
  return response.data;
};

export const createLogicDefinition = async (request: {
  purpose: string; ownerType: string; ownerId: string; name: string;
  effectiveFrom: string; effectiveTo: string; editorHint?: string; expression: object;
}) => {
  const { expression, ...rest } = request;
  const response = await axios.post(BASE, { ...rest, expressionJson: JSON.stringify(expression) })
    .catch(toPricingError('Failed to save.'));
  return response.data;
};

export const saveLogicDefinitionVersion = async (id: number, request: {
  name: string; effectiveFrom: string; effectiveTo: string; editorHint?: string; expression: object;
}) => {
  const { expression, ...rest } = request;
  const response = await axios.post(`${BASE}/${id}/versions`, { ...rest, expressionJson: JSON.stringify(expression) })
    .catch(toPricingError('Failed to save.'));
  return response.data;
};

export const copyLogicDefinitionForward = async (id: number, request: { effectiveFrom?: string; effectiveTo?: string; name?: string } = {}) => {
  const response = await axios.post(`${BASE}/${id}/copy-forward`, request).catch(toPricingError('Failed to copy.'));
  return response.data;
};

export const retireLogicDefinition = async (id: number) => {
  const response = await axios.post(`${BASE}/${id}/retire`).catch(toPricingError('Failed to retire.'));
  return response.data;
};

export const previewLogicExpression = async (
  purpose: string,
  expression: object,
  facts: Record<string, number | string | null>,
  scope: { ownerType?: string; ownerId?: string; asOf?: string; namedValues?: Record<string, unknown> } = {},
) => {
  const response = await axios.post(`${BASE}/preview`, { purpose, expressionJson: JSON.stringify(expression), facts, ...scope })
    .catch(toPricingError('Failed to test.'));
  return response.data;
};

export interface NamedValueDeclaration {
  name: string;
  type: 'Number' | 'Boolean' | 'String' | 'Date';
  unit?: string | null;
  label: string;
  description?: string | null;
  source: 'Catalog' | 'User';
}

export const getNamedValues = async (): Promise<NamedValueDeclaration[]> => {
  const response = await axios.get('/named-values').catch(handleRequestError);
  return response.data;
};

export const createNamedValue = async (request: { name: string; type: string; label: string; unit?: string; description?: string }): Promise<NamedValueDeclaration> => {
  const response = await axios.post('/named-values', request).catch(toPricingError('Failed to create the named value.'));
  return response.data;
};
